import { ReactNode, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useThreadMessages } from "@/app/Threads/hooks/useThreadMessages";
import { useThreadNotes } from "@/app/Threads/hooks/useThreadNotes";
import { useThreadActivity } from "@/app/Threads/hooks/useThreadActivity";
import { useShowThreadActivity } from "@/app/Threads/Components/ThreadActivityProvider";
import { MessageBubble } from "@/app/Threads/Components/MessageBubble";
import { ThreadActivityRow } from "@/app/Threads/Components/ThreadActivityRow";
import { interleaveThreadActivity } from "@/app/Threads/utils/interleaveThreadActivity";
import { threadRecordQuery } from "@/app/Threads/GraphQL/threadQueries";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  ComposeMode,
  MessageInput,
} from "@/app/Threads/Components/MessageInput";
import { MessageDropzone } from "@/app/Threads/Components/MessageDropzone";
import { TypingIndicator } from "@/app/Threads/Components/TypingIndicator";
import { useThreadPresence } from "@/app/Threads/Components/ThreadPresenceProvider";
import { useMessageAttachments } from "@/app/Threads/hooks/useMessageAttachments";
import dayjs from "dayjs";

export function ThreadMessagesView({
  id,
  autoFocus = true,
  emptyState,
  messagesClassName,
  inputClassName,
}: {
  id: string;
  autoFocus?: boolean;
  emptyState?: ReactNode;
  messagesClassName?: string;
  inputClassName?: string;
}) {
  const {
    messages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    sendMessage,
  } = useThreadMessages(id);
  const { addNote } = useThreadNotes(id);
  const attachments = useMessageAttachments();
  const t = useTranslations("thread");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isAdmin } = useTenant();
  const activity = useShowThreadActivity();
  const presence = useThreadPresence();

  const { data: threadData } = useGqlQuery(threadRecordQuery, { id });
  const activityLogs = useThreadActivity(
    threadData?.thread,
    isAdmin && activity?.showActivity === true,
  );

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [id, messages, presence?.typingMembers.length]);

  function handleSend(body: string, filePaths: string[], mode: ComposeMode) {
    const variables = {
      thread_id: id,
      body: body || undefined,
      file_paths: filePaths.length > 0 ? filePaths : undefined,
    };

    if (mode === "note") {
      addNote.mutate(variables, {
        onError: () => toast.error(t("notes.addFailed")),
      });
      return;
    }

    sendMessage.mutate(variables, {
      onError: () => toast.error("Failed to send message"),
    });
  }

  const reversedMessages = messages ? [...messages].reverse() : [];

  const timeline = interleaveThreadActivity(
    reversedMessages,
    activityLogs,
    hasNextPage === true,
  );

  const timelineWithDates = timeline.map((entry, index) => {
    const entryDate = dayjs
      .utc(entry.created_at)
      .local()
      .format("MMMM D, YYYY");
    const prevDate =
      index > 0
        ? dayjs
            .utc(timeline[index - 1].created_at)
            .local()
            .format("MMMM D, YYYY")
        : null;
    return { entry, entryDate, showDate: entryDate !== prevDate };
  });

  return (
    <MessageDropzone onDrop={attachments.add}>
      <div
        ref={scrollContainerRef}
        className={cn("flex-1 overflow-y-auto p-4", messagesClassName)}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            Loading...
          </div>
        ) : timelineWithDates.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            {emptyState ?? (
              <span className="text-sm text-gray-500">
                No messages yet. Start the thread.
              </span>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {hasNextPage ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500 hover:bg-gray-200"
                >
                  {isFetchingNextPage ? "Loading..." : "Load older messages"}
                </button>
              </div>
            ) : null}
            {timelineWithDates.map(({ entry, entryDate, showDate }) => (
              <div key={entry.id}>
                {showDate ? (
                  <div className="my-4 flex justify-center">
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
                      {entryDate}
                    </span>
                  </div>
                ) : null}
                {entry.kind === "activity" ? (
                  <ThreadActivityRow log={entry.log} />
                ) : (
                  <MessageBubble message={entry.message} />
                )}
              </div>
            ))}
          </div>
        )}
        <TypingIndicator threadId={id} />
      </div>

      <MessageInput
        onSend={handleSend}
        onTyping={presence?.notifyTyping}
        attachments={attachments}
        autoFocus={autoFocus}
        className={inputClassName}
      />
    </MessageDropzone>
  );
}
